/**
 * Everything a character has going for them, at one instant, as one value.
 *
 * The single call every consumer makes. Gear goes through `aggregateLoadout`, perks through
 * `aggregatePerks`, the two meet in `combineLoadouts`, and the stat block comes out of
 * `statsWithLoadout`. Nothing downstream should ever do two of those and not the third, which is
 * why they are done here once and handed over together.
 *
 * Derived, never stored. A build is a pure function of a sheet, a rack of equipment and a moment,
 * and caching it in simulation state would put a second copy of the truth somewhere a replay could
 * disagree with. Working it out is a walk over eight slots and twenty five perks, which is cheaper
 * than the bug.
 */

import { aggregateLoadout, combineLoadouts, statsWithLoadout } from '@/sim/inventory/loadoutStats';
import { aggregatePerks } from '@/sim/progression/perkLoadout';
import type { CharacterMoment } from '@/sim/effects/characterMoment';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { EffectBundle } from '@/sim/effects/characterEffects';
import type { Equipment } from '@/sim/inventory/inventoryContract';
import type { Loadout } from '@/sim/inventory/loadoutStats';
import type { StatBlock } from '@/sim/combat/statContract';

export interface CharacterBuild {
  /** The block the damage pipeline should see: earned, plus gear, plus perks, clamped. */
  readonly stats: StatBlock;
  /** Gear and perks as one. Carries the armour and the damage modifiers. */
  readonly loadout: Loadout;
  /** The levers, already combined. The same object as `loadout.effects`, named for reading. */
  readonly effects: EffectBundle;
}

/**
 * Gear first, perks second. The order is the contract `combineLoadouts` documents: damage
 * modifiers apply in list order, so a caller swapping the arguments changes a number.
 */
export function characterBuild(
  sheet: CharacterSheet,
  equipment: Equipment,
  moment: CharacterMoment,
): CharacterBuild {
  return characterBuildWithGear(sheet, aggregateLoadout(equipment), moment);
}

/**
 * The same build, from gear already added up rather than from the rack it came off.
 *
 * The seam a machine that cannot see the rack builds through. A guest predicting its own body has
 * the host's gear as a `Loadout` off the wire and no items to walk, and re-deriving one from an
 * empty rack would be a build the host never held: see
 * [netOwnGear](../../modes/shootingRange/net/entities/netOwnGear.ts).
 *
 * Perks stay local on both sides and are worked out here from the sheet and the moment, which is
 * what a wire cannot carry: a perk that reads whether its owner is moving has a different answer on
 * every replayed tick, so a bundle sent once would freeze the one it had when the packet left.
 */
export function characterBuildWithGear(
  sheet: CharacterSheet,
  gear: Loadout,
  moment: CharacterMoment,
): CharacterBuild {
  const loadout = combineLoadouts(gear, aggregatePerks(sheet.progress, moment));

  return { stats: statsWithLoadout(sheet.stats, loadout), loadout, effects: loadout.effects };
}
